/**
 * The API's wire shapes, as the browser sees them.
 *
 * A hand-written port of `groundtruth_core/served.py` and the parts of
 * `model.py` that reach a client, deliberately rather than generated from
 * `/openapi.json`: the QGIS plugin holds the same port in Python, and both
 * exist because the response is the contract. When the schema grows, the
 * fields arrive here as optional and old servers keep parsing, exactly the way
 * `served.py` uses `.get()` for everything added after 0.3.
 *
 * Nothing here computes anything. Verdicts, closure, areas and geometry are
 * the server's judgement; this file names them so the browser can render what
 * it was told.
 */

export type Verdict = "PASS" | "REVIEW" | "FAIL";

export interface BearingModel {
  ns: "N" | "S";
  degrees: number;
  minutes: number;
  seconds: number;
  ew: "E" | "W";
}

export interface Call {
  call_type: string;
  verbatim_text: string;
  confidence?: string | null;
  monument?: string | null;
  adjoiner?: string | null;
  bearing?: BearingModel | null;
  distance?: number | null;
  chord_bearing?: BearingModel | null;
  chord_length?: number | null;
  radius?: number | null;
  arc_length?: number | null;
  curve_direction?: string | null;
}

export interface RecordingRef {
  book?: string | null;
  page?: string | null;
  instrument?: string | null;
  note?: string | null;
}

export interface LotBlockRef {
  verbatim_text?: string;
  subdivision?: string | null;
  lot?: string | null;
  block?: string | null;
  plat_book?: string | null;
  plat_page?: string | null;
}

export interface DeedMetadata {
  grantors?: string[];
  grantees?: string[];
  recording?: RecordingRef | null;
  prior_references?: RecordingRef[];
  transfer_date?: string | null;
  county?: string | null;
  state?: string | null;
  lot_block?: LotBlockRef[];
}

export interface EasementDescription {
  easement_type?: string | null;
  width_ft?: number | null;
  term?: string | null;
  exclusive?: boolean | null;
  rights?: string[];
  servient_reference?: RecordingRef | null;
}

export interface AliquotDescription {
  verbatim_text?: string;
  parts?: string[];
  section?: number;
  township_number?: number;
  township_direction?: string;
  range_number?: number;
  range_direction?: string;
}

export interface Tract {
  name: string;
  pob_description?: string;
  calls?: Call[];
  tie_calls?: Call[];
  stated_acreage?: number | null;
  stated_area_sqft?: number | null;
  stated_area_sqm?: number | null;
  stated_hectares?: number | null;
  distance_unit?: string;
  is_exception?: boolean;
  description_type?: string;
  easement?: EasementDescription | null;
  aliquot?: AliquotDescription | null;
}

export interface PlatDescription {
  subdivision?: string | null;
  lots?: string[];
  blocks?: string[];
  surveyor?: string | null;
  verbatim_text?: string;
  [key: string]: unknown;
}

export interface DeedExtraction {
  document_kind: string;
  legal_description_found: boolean;
  tracts?: Tract[];
  basis_of_bearings?: string | null;
  notes?: string | null;
  metadata?: DeedMetadata | null;
  plat?: PlatDescription | null;
  as_built?: PlatDescription | null;
}

/** A computed traverse in its local plane: POB at the origin, feet. */
export interface TractGeometry {
  vertices: [number, number][];
  misclosure: number;
  perimeter_ft: number;
  /** Null means the traverse closes exactly; JSON cannot carry Infinity. */
  closure_denominator: number | null;
  acres: number;
}

/** Aliquot geometry located on the earth, straight from the PLSS fabric. */
export interface GeorefGeometry {
  crs: string;
  rings: [number, number][][];
  acres: number;
  plss_id: string;
  source: string;
}

export interface DocumentVerdict {
  document: string;
  status: "REVIEW" | "FAIL";
  reasons: string[];
  summary: string;
}

export interface ServerVerdict {
  tract: string;
  status: Verdict;
  reasons: string[];
  closure_ratio?: string | null;
  computed_acres?: number | null;
  computed_sqft?: number | null;
  computed_sqm?: number | null;
  computed_hectares?: number | null;
  distance_unit?: string;
  geometry?: TractGeometry | null;
  georef?: GeorefGeometry | null;
  /** [east_ft, north_ft] from the commencement monument to this POB. */
  tie_offset?: [number, number] | null;
  anchor?: string | null;
  /** Filled only by `/v1/locate`; null on every extract response. */
  location?: SuggestedLocation | null;
  /**
   * An operator supplied a better reading and this verdict was recomputed
   * over it. The status still means exactly what it always means.
   */
  corrected?: boolean;
  /**
   * Geometry was fitted by a compass rule after the fact. **The status did
   * not move**: it judges the recorded description, which still miscloses.
   */
  adjusted?: boolean;
}

/**
 * Where the service thinks a tract sits, and what it compared to say so.
 *
 * A suggestion, never a placement: `status` stays REVIEW even when every
 * check agreed, and `pob` is null whenever the suggestion failed its own
 * containment test. A null `pob` with reasons is the useful answer, not an
 * empty one, so the UI renders it rather than hiding it.
 */
export interface SuggestedLocation {
  crs?: string;
  /** lon/lat, or null when no position could be established. */
  pob?: [number, number] | null;
  /** Null when nothing solved rotation, which the PLSS corner never does. */
  rotation_deg?: number | null;
  source?: string;
  basis?: string;
  plss_id?: string;
  status?: string;
  reasons?: string[];
}

export interface Attestation {
  document_sha256: string;
  extracted_at: string;
  signature: string;
}

export interface ExtractResponse {
  extraction: DeedExtraction;
  groundtruth_verdicts: ServerVerdict[];
  document_verdict?: DocumentVerdict | null;
  usage?: { input_tokens: number; output_tokens: number; seconds: number };
  engine_version?: string;
  document?: string;
  /** How the source arrived: "document" for a PDF or image, "text" for a
   *  pasted legal description. Absent on responses from older servers, which
   *  only ever read documents. */
  input_kind?: "document" | "text";
  attestation?: Attestation;
  /** The operator log. Empty on everything `/v1/extract` returns. */
  corrections?: Correction[];
  adjustments?: Adjustment[];
}

/** What an operator may correct: measured values, never the document's words. */
export type CorrectableField =
  | "distance"
  | "chord_length"
  | "radius"
  | "arc_length"
  | "bearing"
  | "chord_bearing"
  | "curve_direction";

/**
 * One changed field, and what it read before.
 *
 * `before` is the staleness guard and the certificate's diff at once: nothing
 * overwrites the extraction, so what the document was extracted as survives
 * beside what an operator says it should read.
 */
export interface Correction {
  tract: number;
  call: number;
  field: CorrectableField;
  before: unknown;
  after: unknown;
  kind?: "boundary" | "tie";
  by?: string;
  at?: string;
  note?: string;
}

/**
 * A disclosed best fit over courses that do not close.
 *
 * Never moves a verdict. The recorded description still miscloses by
 * `misclosure_before`, and the verdict on the tract still says so.
 */
export interface Adjustment {
  tract: number;
  method: string;
  misclosure_before: number;
  perimeter: number;
  by?: string;
  at?: string;
}

/**
 * A response and the exact bytes it arrived as.
 *
 * The attestation signs the serialized body, so posting back to
 * `/v1/certificate` has to send those bytes rather than a re-serialization:
 * `JSON.parse` then `JSON.stringify` reorders nothing in practice but drops
 * nothing either, and betting the certificate on that is a bet with no upside.
 */
export interface HeldResponse {
  parsed: ExtractResponse;
  raw: string;
}

export interface ExtractJob {
  /** `job`, not `job_id`. The API spells it that way in both the submit and
   *  the poll response; guessing the other spelling polled
   *  `/v1/extracts/undefined`, which the server correctly refused. Every
   *  field here was read off the wire rather than inferred. */
  job: string;
  status: "queued" | "running" | "succeeded" | "failed";
  document?: string;
  submitted_at?: string;
  poll_url?: string;
  error?: string | null;
  result?: ExtractResponse | null;
}

export interface BatchItemSummary {
  document: string;
  status: string;
  error?: string | null;
}

export interface BatchStatus {
  batch: string;
  /** "in_progress" | "ended" | "failed". */
  status: string;
  documents: number;
  succeeded: number;
  failed: number;
  submitted_at?: string;
  completed_at?: string | null;
  /** Present once the batch has ended. */
  results_url?: string | null;
  items: BatchItemSummary[];
}

/** One document's outcome. Exactly one of `result` and `error` is set. */
export interface BatchResult {
  document: string;
  status: string;
  result?: ExtractResponse | null;
  error?: string | null;
}

export interface KeyRow {
  prefix: string;
  name: string;
  monthly_cap: number;
  created_at: string;
  revoked_at?: string | null;
  documents: number;
  included_docs?: number | null;
  pool_docs?: number | null;
  lifetime_documents?: number | null;
  /** "free_signup" for a granted allowance, null for everything bought. */
  grant_kind?: string | null;
}

/**
 * What minting or rotating a key answers: the plaintext, once.
 *
 * The server keeps only the digest, so nothing can show this a second time.
 * Losing it is not an error state with a recovery flow; it is what the rotate
 * button is for.
 */
export interface MintResponse {
  api_key: string;
  prefix: string;
  name: string;
  monthly_cap: number;
  pool_docs?: number | null;
  created_at: string;
}

export interface WhoAmI {
  email: string;
  superadmin: boolean;
  stripe_customer_id?: string | null;
  active_keys: number;
}

/** Verdict badge colors, matched to the plugin's `theme.py` exactly so the
 *  product reads as one thing across QGIS, the browser, and the site. */
export const VERDICT_COLOR: Record<Verdict, string> = {
  PASS: "#0a7d32",
  REVIEW: "#8a6500",
  FAIL: "#c0392b",
};

/**
 * One document this account ran, whichever way it went in.
 *
 * `result_available` is the field the page exists for. It says what the API
 * holds right now, so a false is the answer to "where did my result go"
 * rather than a gap in the list: the retention sweep took it, and nothing
 * here brings it back.
 */
export interface RunRow {
  id: string;
  document: string;
  status: string;
  /** "single" for one document, "batch" for one inside a folder run. */
  source: string;
  /** The job id, or the batch a batched document sits in. */
  reference: string;
  key_prefix: string;
  submitted_at: string;
  finished_at?: string | null;
  result_available: boolean;
  document_sha256?: string | null;
  error?: string | null;
  /** How many of this run's tracts somebody has put on a map. */
  placements?: number;
}

/**
 * One stored position: where an operator put a tract, in WGS 84.
 *
 * Deliberately outside `ExtractResponse`, and the API keeps it there. The
 * attestation covers what the service computed and can defend; where a parcel
 * sits on the earth is the operator's own claim, checked by nobody, so it
 * travels beside the signed result rather than inside it.
 */
export interface SavedPlacement {
  /** Which document of the run, for a batch reference that covers many. */
  document?: string;
  tract_index: number;
  lon: number;
  lat: number;
  rotation_deg: number;
}

export interface PlacementListResponse {
  reference: string;
  placements: SavedPlacement[];
}

export interface RunListResponse {
  runs: RunRow[];
}
